/* METTA product page: no size until the shopper picks one (SKIMS).
   The theme opens on the first available variant with its size chip lit.
   Here the size chips start blank and every add button in the product
   section (the page button and the phone sticky bar) reads "Veldu stærð";
   pressing one scrolls to the sizes instead of adding. The first size tap
   hands the button back to the theme ("Bæta í körfu", sold out, …).
   The page renders this way from the server too (snippets/metta-size-blank
   .liquid), so no size shows lit before this runs. A ?variant= link (a
   size-filtered collection's cards, a cart line) also opens blank: the
   size is always the shopper's own tap (21 Oct 2026); the variant's other
   options (Tegund) still apply.
   The quick-view drawer follows the same rule on every page: it opens with
   no size and "Veldu stærð" (loaded site-wide from layout/theme.liquid).
   A colour swapped in place (metta-pdp-siblings.js) keeps the size picked
   on the colour before, when the new colour has it to sell. It used to
   light for a moment and clear. */
(() => {
  const SIZE = /^(stærð|size|størrelse|storlek)$/i;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const sizeGroup = (section) => Array.from(section.querySelectorAll('.product__color-chips[data-option-buttons]')).find((g) => {
    const label = g.closest('.product__option')?.querySelector('.product__label');
    const name = (label?.firstChild?.textContent || '').replace(':', '').trim();
    return SIZE.test(name) && g.querySelectorAll('[data-button]').length > 1;
  });

  // The size on screen, read by metta-pdp-siblings.js before a colour swap;
  // set by it as the carry the next product section starts from.
  window.mettaSize = {
    chosen: (section) => {
      const chip = !section.classList.contains('metta-needs-size') && sizeGroup(section)?.querySelector('[data-button].selected');
      return chip ? chip.dataset.optionValue : '';
    },
    carry: '',
  };

  // The theme builds the product form a request after the section mounts
  // (theme.js getProduct); a chip tapped before then lights but changes
  // nothing.
  const formReady = (section) => new Promise((resolve) => {
    const start = performance.now();
    const tick = () => {
      const ready = window.Shopify?.theme?.sections?.instances?.some((i) => i.container === section && i.product?.productForm);
      if (ready || !section.isConnected || performance.now() - start > 4000) resolve(ready);
      else requestAnimationFrame(tick);
    };
    tick();
  });

  const init = (section) => {
    if (section.dataset.mettaSize) return;
    section.dataset.mettaSize = '1';
    const quick = section.hasAttribute('data-is-quick-view');
    const carry = quick ? '' : window.mettaSize.carry;
    if (!quick) window.mettaSize.carry = '';

    const group = sizeGroup(section);
    if (!group) return;

    const option = group.closest('.product__option');
    const chips = Array.from(group.querySelectorAll('[data-button]'));
    const valueLabel = option.querySelector('[data-selected-value-for-option]');
    const buttons = () => Array.from(section.querySelectorAll('[data-add-to-cart]'));
    // The sticky bar names the size (the theme's first one until a pick);
    // unpicked it reads "Stærð", like the label over the sizes.
    const barSize = () => section.querySelector('.sticky-atc-bar__meta-options');
    const sizeName = (option.querySelector('.product__label')?.firstChild?.textContent || '').replace(':', '').trim();
    let chosen = false;
    // A variant in an incoming URL is not a choice. Record the shopper's
    // actual choice on this history entry so Back/Forward can restore it
    // without carrying a different size from the colour being left.
    const remember = () => {
      if (quick || !section.isConnected || section.getAttribute('aria-busy') === 'true') return;
      history.replaceState({ ...history.state, mettaSize: window.mettaSize.chosen(section) }, '');
    };

    // The carried size, if this colour sells it: lit at once so it never
    // blinks off, then picked for real once the theme's form is up. Until
    // then the add button still goes to the sizes rather than adding the
    // theme's default size.
    let carried = (() => {
      const chip = carry && chips.find((c) => c.dataset.optionValue === carry);
      if (!chip || chip.classList.contains('disabled')) return null;
      const held = (section.dataset.mettaStockHeld || '').split(',');
      let variants = [];
      try { variants = JSON.parse(section.closest('.shopify-section')?.querySelector('script[id^="ProductJson"]')?.textContent || '{}').variants || []; } catch { return null; }
      return variants.some((v) => v.available && v.options.includes(carry) && !held.includes(String(v.id))) ? chip : null;
    })();

    const blank = () => {
      if (carried && !chosen) {
        chips.forEach((c) => c.classList.toggle('selected', c === carried));
        if (valueLabel) valueLabel.textContent = carried.dataset.optionValue;
        return;
      }
      chips.forEach((c) => c.classList.remove('selected'));
      if (valueLabel) valueLabel.textContent = '';
      section.classList.add('metta-needs-size');
      if (barSize() && sizeName) barSize().textContent = sizeName;
      buttons().forEach((b) => {
        const label = b.dataset.langChoose || 'Veldu stærð';
        const text = b.querySelector('[data-add-to-cart-text]');
        if (text) text.textContent = label;
        b.setAttribute('aria-label', label);
        b.removeAttribute('disabled');
        b.removeAttribute('aria-disabled');
      });
    };

    // After the theme has handled the tap: light the chip, name the size, and
    // give the button its real label if the theme had no variant change to make.
    const restore = (chip) => {
      section.classList.remove('metta-needs-size');
      option.classList.remove('metta-size-nudge');
      chip.classList.add('selected');
      if (valueLabel) valueLabel.textContent = chip.dataset.optionValue;
      // The theme renames it on a variant change; the size it opened on
      // isn't one, so name that here.
      if (barSize()?.textContent.trim() === sizeName) barSize().textContent = chip.dataset.optionValue;
      buttons().forEach((b) => {
        const text = b.querySelector('[data-add-to-cart-text]');
        if (text && text.textContent.trim() === (b.dataset.langChoose || 'Veldu stærð')) {
          text.textContent = b.dataset.langAvailable;
        }
        b.setAttribute('aria-label', text ? text.textContent.trim() : b.dataset.langAvailable);
      });
      remember();
    };

    group.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-button]');
      if (!chip || chosen) return;
      chosen = true;
      setTimeout(() => restore(chip), 0);
    }, true);

    // Another option changing (a colour within the product) re-lights the
    // theme's default size; keep it blank until a size is tapped.
    section.addEventListener('change', () => setTimeout(() => {
      if (!chosen) blank();
      remember();
    }, 0), true);

    // Unchosen, an add button or form submit goes to the sizes.
    const toSizes = (e) => {
      if (chosen) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      option.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      option.classList.add('metta-size-nudge');
    };
    // The drawer is rebuilt on every open, so it listens on itself rather
    // than adding a document listener each time.
    const root = quick ? section : document;
    root.addEventListener('click', (e) => {
      const b = e.target.closest('[data-add-to-cart]');
      if (b && section.contains(b)) toSizes(e);
    }, true);
    root.addEventListener('submit', (e) => {
      if (section.contains(e.target)) toSizes(e);
    }, true);

    blank();
    remember();
    if (carried) formReady(section).then((ready) => {
      if (chosen || !section.isConnected) return;
      if (ready) carried.click();
      else { carried = null; blank(); }
    });
    // The theme may still be setting up its form; blank again once it has.
    // A product swapped in for another colour arrives after load.
    if (quick || document.readyState === 'complete') requestAnimationFrame(() => { if (!chosen) blank(); });
    else window.addEventListener('load', () => { if (!chosen) blank(); }, { once: true });
  };

  const SECTIONS = '.product--main[data-section-type="product"], [data-is-quick-view][data-section-type="product"]';
  const run = () => document.querySelectorAll(SECTIONS).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);

  // The drawer's contents are replaced on every open and colour change.
  const drawer = document.querySelector('.quick-view-modal__content');
  if (drawer) {
    window.__mettaSizeObserver = new MutationObserver(run);
    window.__mettaSizeObserver.observe(drawer, { childList: true });
  }
})();
